import { useState } from 'react';
import { ArrowUpRight, Search } from 'lucide-react';

import { EXTERNAL_TOOLS, ExternalTool } from '../../../config/external-tools';
import { InternalModule } from '../launch.constants';
import {
  SearchWrapper,
  SearchInputBox,
  SearchIconWrapper,
  SearchInput,
  SearchDropdown,
  SearchDropdownItem,
  SearchDropdownName,
  SearchDropdownDescription,
  SearchEmpty,
  ListItemIcon,
  SsoBadge,
} from '../styles';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
type SearchResult =
  | { kind: 'module'; id: string; name: string; description: string; icon: React.ElementType; color: string; path: string }
  | { kind: 'tool';   id: string; name: string; description: string };

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────
interface SearchBarProps {
  visibleModules: InternalModule[];
  onOpenModule: (path: string) => void;
  onOpenTool: (tool: ExternalTool) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
export const SearchBar = ({ visibleModules, onOpenModule, onOpenTool }: SearchBarProps) => {
  const [query, setQuery]           = useState('');
  const [dropdownOpen, setDropdown] = useState(false);

  const q = query.trim().toLowerCase();

  const results: SearchResult[] = q
    ? [
        ...visibleModules
          .filter((m) => m.name.toLowerCase().includes(q))
          .map((m): SearchResult => ({ kind: 'module', id: m.id, name: m.name, description: m.description, icon: m.icon, color: m.color, path: m.path })),
        ...EXTERNAL_TOOLS
          .filter((t) => t.name.toLowerCase().includes(q))
          .map((t): SearchResult => ({ kind: 'tool', id: t.id, name: t.name, description: t.description })),
      ]
    : [];

  const handleSelect = (result: SearchResult) => {
    setQuery('');
    setDropdown(false);
    if (result.kind === 'module') {
      onOpenModule(result.path);
    } else {
      const tool = EXTERNAL_TOOLS.find((t) => t.id === result.id);
      if (tool) onOpenTool(tool);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setQuery('');
      setDropdown(false);
    }
  };

  // Delay para permitir que el onMouseDown del item se registre antes del blur
  const handleBlur = () => setTimeout(() => setDropdown(false), 150);

  return (
    <SearchWrapper>
      <SearchInputBox>
        <SearchIconWrapper>
          <Search size={16} strokeWidth={1.75} />
        </SearchIconWrapper>
        <SearchInput
          type="text"
          placeholder="Buscar módulo o herramienta..."
          value={query}
          onChange={(e) => { setQuery(e.target.value); setDropdown(true); }}
          onFocus={() => query && setDropdown(true)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          aria-label="Buscar"
        />
      </SearchInputBox>

      {dropdownOpen && q && (
        <SearchDropdown role="listbox">
          {results.length === 0 ? (
            <SearchEmpty>Sin resultados para "{query}"</SearchEmpty>
          ) : (
            results.map((result) => {
              if (result.kind === 'module') {
                const Icon = result.icon;
                return (
                  <SearchDropdownItem key={result.id} role="option" onMouseDown={() => handleSelect(result)}>
                    <ListItemIcon $color={result.color} style={{ width: 26, height: 26, minWidth: 26 }}>
                      <Icon size={14} strokeWidth={1.75} />
                    </ListItemIcon>
                    <SearchDropdownName>{result.name}</SearchDropdownName>
                    <SearchDropdownDescription>{result.description}</SearchDropdownDescription>
                  </SearchDropdownItem>
                );
              }
              return (
                <SearchDropdownItem key={result.id} role="option" onMouseDown={() => handleSelect(result)}>
                  <ListItemIcon $color="#7c3aed" style={{ width: 26, height: 26, minWidth: 26 }}>
                    <ArrowUpRight size={14} strokeWidth={1.75} />
                  </ListItemIcon>
                  <SearchDropdownName>{result.name}</SearchDropdownName>
                  <SearchDropdownDescription>{result.description}</SearchDropdownDescription>
                  <SsoBadge>SSO</SsoBadge>
                </SearchDropdownItem>
              );
            })
          )}
        </SearchDropdown>
      )}
    </SearchWrapper>
  );
};
